<template>
  <div class="teacher-approval">
    <v-subheader class="py-0 d-flex justify-space-between rounded-lg">
      <TitleComponent title="Aprobarea cererilor de modificare note" class="hide-on-mobile" />
      <v-spacer></v-spacer>
      <ErrorAlert :message="errorMessage" @clear-error="clearMessage" />
      <SuccessAlert :message="successMessage" @clear-error="clearMessage" />
    </v-subheader>
    <br />
    
    <v-row>
      <v-col>
        <v-card>
          <v-card-title class="d-flex align-center pe-2">
            <div>&nbsp; {{ getTableTitle() }}</div>
            <v-spacer></v-spacer>
            <SearchBar @search="handleSearch" />
            <v-spacer></v-spacer>
            <ColumnSelect
              :headers="headers"
              :updateVisibleColumns="updateVisibleColumns"
            />
          </v-card-title>

          <v-divider></v-divider>
          <v-data-table
            :mobile-breakpoint="0"
            :headers="visibleHeaders"
            :items="paginatedItems"
            :sort-by="pagination.sortBy"
            :sort-desc="pagination.sortDesc"
            :loading="isLoading"
            loading-text="Incărcare... Aștepta-ți vă rog"
            class="elevation-1"
            :options.sync="pagination"
            :server-items-length="filteredData.length"
            @update:options="handleTableOptionsUpdate"
            :multi-sort="false"
            :must-sort="true"
            sortable
          >
            <template v-slot:item="{ item, index }">
              <tr :class="{ 'light-green-row': index % 2 !== 0 }">
                <td
                  v-for="(header, hIndex) in visibleHeaders"
                  :key="hIndex"
                  :style="header.style ? header.style : {}"
                  :class="{ 'red-row': item.status === 'Respins' }"
                >
                  <template v-if="header.value === 'comments'">
                    <div v-if="item.requestId" class="comments-wrapper">
                      <v-timeline dense class="compact-timeline">
                        <v-timeline-item
                          v-for="(comment, index) in item.commentsHistory || []"
                          :key="index"
                          small
                          :color="getStatusColor(comment.status)"
                          class="compact-item"
                        >
                          <div class="comment-header">
                            <strong class="user-name">{{ comment.user }}</strong>
                            <v-chip x-small :color="getStatusColor(comment.status)" class="ml-1">
                              {{ comment.status }}
                            </v-chip>
                          </div>
                          <div class="comment-body">{{ comment.comment }}</div>
                          <div class="comment-date">{{ formatDate(comment.date) }}</div>
                        </v-timeline-item>
                      </v-timeline>
                    </div>
                    <div v-else class="no-comments">-</div>
                  </template>
                  <template v-else-if="header.value === 'status'">
                    <v-chip :color="getStatusColor(item.status)" small>
                      {{ item.status }}
                    </v-chip>
                  </template>
                  <template v-else>
                    {{ getTableCellValue(item, header, index) }}
                  </template>
                  <template v-if="header.value === 'actions'">
                    <v-btn
                      v-if="canApproveRequest(item)"
                      color="success"
                      outlined
                      small
                      @click="onViewClick(item)"
                      class="mr-2"
                    >
                      <v-icon small left>mdi-check</v-icon>
                      {{ getApprovalButtonText(item) }}
                    </v-btn>
                    <v-btn
                      v-else
                      color="primary"
                      outlined
                      small
                      @click="onViewClick(item)"
                      class="mr-2"
                    >
                      <v-icon small left>mdi-eye</v-icon>
                      Detalii
                    </v-btn>
                  </template>
                </td>
              </tr>
            </template>
            
            <template v-slot:no-data>
              <div class="pa-6 text-center">
                <p v-if="isLoading">Încărcare date...</p>
                <p v-else>Nu există cereri pentru aprobat.</p>
              </div>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>
    
    <TeacherApprovalDetails
      ref="approvalDetails"
      @update-data="refreshApprovalData"
      @error="handleError"
      @success="handleSuccess"
    />
  </div>
</template>

<script>
import TitleComponent from "@/components/elements/TitleComponent.vue";
import ColumnSelect from "@/components/elements/ColumnSelect.vue";
import ErrorAlert from "@/components/alerts/ErrorAlert.vue";
import SuccessAlert from "@/components/alerts/SuccessAlert.vue";
import SearchBar from "@/components/elements/SearchBar.vue";
import TeacherApprovalDetails from "./TeacherApprovalDetails.vue";
import * as api from "../request/api";
import * as helper from "@/helper/helper.js";
import { mapGetters } from "vuex";

// Response type IDs based on your system
const ResponseType = {
  TEACHER_CREATED: 13,       // Emis de profesor
  DEAN_APPROVED: 14,         // Aprobat de Decan
  RECTOR_APPROVED: 3,        // Aprobat de Rector
  COMPLETED: 9,              // Îndeplinit
  REJECTED: 4,               // Respins
  IN_PROCESS: 8              // În proces lucrare
};

export default {
  name: "TeacherApprovalPage",
  components: {
    TitleComponent,
    ColumnSelect,
    ErrorAlert,
    SuccessAlert,
    SearchBar,
    TeacherApprovalDetails,
  },
  data() {
    return {
      headers: [],
      approvalData: [],
      visibleHeaders: [],
      selectedRequest: null,
      search: "",
      errorMessage: "",
      successMessage: "",
      isLoading: true,
      commentsCache: {},
      commentLoading: false,
      pagination: {
        page: 1,
        itemsPerPage: 10,
        sortBy: ["date"],
        sortDesc: [true]
      }
    };
  },
  computed: {
    ...mapGetters(["getUser"]),
    isAdmin() {
      return this.getUser.role === "admin";
    },
    isDean() {
      // Check if user has 'usersef' role first
      const isUserSef = this.getUser.role === "usersef";
      if (!isUserSef) return false;
      
      // Get department
      const dept = this.getUser.department;
      
      // Define exact faculty names that qualify as dean (with variations)
      const deanFaculties = [
        'Facultatea de Litere',
        'Facultatea de Științe Reale, Economice și ale Mediului',
        'Facultatea de Ştiinţe Reale, Economice şi ale Mediului', // Alternative encoding
        'Facultatea de Științe ale Educației, Psihologie și Arte',
        'Facultatea de Ştiinţe ale Educaţiei, Psihologie şi Arte', // Alternative encoding
        'Facultatea de Drept și Științe Sociale',
        'Facultatea de Drept şi Ştiinţe Sociale' // Alternative encoding
      ];
      // Check for exact match with one of the dean faculty names
      return deanFaculties.some(faculty => {
        // Normalize both strings to handle different encoding of diacritics
        const normalizedDept = dept?.toLowerCase().normalize('NFKD') || '';
        const normalizedFaculty = faculty.toLowerCase().normalize('NFKD');
        
        return normalizedDept === normalizedFaculty || 
               dept === faculty || 
               (dept && dept.replace(/ț/g, 'ţ').replace(/ș/g, 'ş') === faculty) ||
               dept === faculty.replace(/ţ/g, 'ț').replace(/ş/g, 'ș');
      });
      
    },
    isRector() {
      return this.getUser.role === "rector";
    },
    isOperator() {
      return this.getUser.role === "operator";
    },
    isTeacher() {
      return this.getUser.role === "profesor";
    },
    // Check if the user has dual roles (dean and professor)
    isAlsoProfessor() {
      return this.isDean && this.getUser.hasRole && this.getUser.hasRole.includes("profesor");
    },
    
    filteredData() {
      if (!this.approvalData || !this.approvalData.length) return [];
      
      return this.approvalData.filter(item => {
        if (!this.search) return true;
        
        const searchLower = this.search.toLowerCase();
        return (
          (item.user && item.user.toLowerCase().includes(searchLower)) ||
          (item.facultatea && item.facultatea.toLowerCase().includes(searchLower)) ||
          (item.grupa && item.grupa.toLowerCase().includes(searchLower)) ||
          (item.disciplina && item.disciplina.toLowerCase().includes(searchLower)) ||
          (item.cause && item.cause.toLowerCase().includes(searchLower)) ||
          (item.status && item.status.toLowerCase().includes(searchLower))
        );
      });
    },
    
    paginatedItems() {
      let sortedData = [...this.filteredData];
      
      if (this.pagination.sortBy && this.pagination.sortBy.length > 0) {
        const sortKey = this.pagination.sortBy[0];
        const sortDesc = this.pagination.sortDesc[0];
        
        sortedData.sort((a, b) => {
          let aValue, bValue;
          
          if (sortKey === 'date') {
            aValue = new Date(a.date || a.created_at).getTime();
            bValue = new Date(b.date || b.created_at).getTime();
          } else if (sortKey === 'id' || sortKey === 'requestId') {
            aValue = parseInt(a[sortKey]);
            bValue = parseInt(b[sortKey]);
          } else {
            aValue = a[sortKey];
            bValue = b[sortKey];
          }
          
          if (aValue < bValue) return sortDesc ? 1 : -1;
          if (aValue > bValue) return sortDesc ? -1 : 1;
          return 0;
        });
      }
      
      const startIndex = (this.pagination.page - 1) * this.pagination.itemsPerPage;
      const endIndex = startIndex + this.pagination.itemsPerPage;
      return sortedData.slice(startIndex, endIndex);
    },
    // Get user's department/faculty
    userDepartment() {
      return this.getUser.department || '';
    },
  },
  methods: {
    // Get appropriate table title based on role
    getTableTitle() {
      if (this.isAdmin) {
        return 'Toate cererile de modificare note';
      } else if (this.isDean) {
        return 'Cereri pentru aprobarea decanului';
      } else if (this.isRector) {
        return 'Cereri pentru aprobarea rectorului';
      } else if (this.isOperator) {
        return 'Cereri pentru procesare';
      }
      
      return 'Tabelul Aprobărilor';
    },
    
    // Determine if user can approve this specific request
    canApproveRequest(item) {
      if (!item) return false;
      
      // Dean can approve teacher created requests
      if (this.isDean && item.status_id === ResponseType.TEACHER_CREATED) {
        return true;
      }
      
      // Rector can approve dean approved requests
      if (this.isRector && item.status_id === ResponseType.DEAN_APPROVED) {
        return true;
      }
      
      // Operators can mark rector approved requests as in process or complete
      if (this.isOperator && 
         (item.status_id === ResponseType.RECTOR_APPROVED || 
          item.status_id === ResponseType.IN_PROCESS)) {
        return true;
      }
      
      // Admin can approve any request that isn't completed or rejected
      if (this.isAdmin && 
          item.status_id !== ResponseType.COMPLETED && 
          item.status_id !== ResponseType.REJECTED) {
        return true;
      }
      
      return false;
    },
    
    // Get appropriate button text based on item status and user role
    getApprovalButtonText(item) {
      if (!item) return 'Aprobare';
      
      if (this.isOperator) {
        if (item.status_id === ResponseType.RECTOR_APPROVED) {
          return 'Procesare';
        } else if (item.status_id === ResponseType.IN_PROCESS) {
          return 'Finalizare';
        }
      }
      
      return 'Aprobare';
    },
    
    async fetchApprovalData() {
      this.isLoading = true;
      try {
        if (this.isAdmin) {
          // Admin sees all requests
          this.approvalData = await api.getAllTeacherRequests();
        } else if (this.isDean) {
          // Use the server-side filtering endpoint for dean's faculty
          const approvalNeeded = await api.getTeacherApprovalDataByTypeAndDepartment(
            ResponseType.TEACHER_CREATED, 
            this.userDepartment
          );
          
          // If dean is also a professor, include their own requests
          let ownRequests = [];
          if (this.isAlsoProfessor) {
            ownRequests = await api.getTeacherRequestsByUserId(this.getUser.userId);
          }
          
          this.approvalData = [...approvalNeeded, ...ownRequests];
        } else if (this.isRector) {
          // Rector sees requests waiting for rector approval
          const approvalNeeded = await api.getTeacherApprovalDataByType(ResponseType.DEAN_APPROVED);
          const approvedByRector = await api.getTeacherApprovalDataByType(ResponseType.RECTOR_APPROVED);
          
          this.approvalData = [...approvalNeeded, ...approvedByRector];
        } else if (this.isOperator) {
          // Operator sees requests approved by rector and in processing
          const rectorApproved = await api.getTeacherApprovalDataByType(ResponseType.RECTOR_APPROVED);
          const inProcess = await api.getTeacherApprovalDataByType(ResponseType.IN_PROCESS);
          const completed = await api.getTeacherApprovalDataByType(ResponseType.COMPLETED);
          
          this.approvalData = [...rectorApproved, ...inProcess, ...completed];
        } else {
          this.approvalData = [];
        }
        
        // Fetch comments for the approval data
        const requestIds = this.approvalData
          .filter(item => item.requestId)
          .map(item => item.requestId);
          
        if (requestIds.length > 0) {
          await this.fetchCommentsInBulk(requestIds);
        }
        
      } catch (error) {
        console.error("Error fetching teacher approval data:", error);
        this.handleError("Eroare la încărcarea datelor de aprobare");
      } finally {
        this.isLoading = false;
      }
    },
    
    async fetchCommentsInBulk(requestIds) {
      if (this.commentLoading || requestIds.length === 0) return;
      
      try {
        this.commentLoading = true;
        
        const idsToFetch = requestIds.filter(id => !this.commentsCache[id]);
        
        if (idsToFetch.length === 0) {
          this.approvalData.forEach(item => {
            if (item.requestId && this.commentsCache[item.requestId]) {
              item.commentsHistory = this.commentsCache[item.requestId];
            }
          });
          this.commentLoading = false;
          return;
        }
        
        const commentsData = await api.getBulkTeacherRequestCommentsHistory(idsToFetch);
        
        Object.entries(commentsData).forEach(([requestId, comments]) => {
          const numericId = parseInt(requestId, 10);
          this.commentsCache[numericId] = comments;
          
          const items = this.approvalData.filter(item => item.requestId === numericId);
          items.forEach(item => {
            item.commentsHistory = comments;
          });
        });
      } catch (error) {
        console.error("Error fetching bulk comments for teacher requests:", error);
      } finally {
        this.commentLoading = false;
      }
    },
    
    async refreshApprovalData() {
      await this.fetchApprovalData();
    },
    
    onViewClick(item) {
      this.selectedRequest = { ...item };
      this.$refs.approvalDetails.open(this.selectedRequest);
    },
    
    updateVisibleColumns() {
      this.visibleHeaders = this.headers.filter((header) => header.visible);
    },
    
    getTableCellValue(item, header, index) {
      if (header.value === "index") return index + 1;
      if (header.value === "user") return item.user;
      if (header.value === "facultatea") return item.facultatea;
      if (header.value === "grupa") return item.grupa;
      if (header.value === "disciplina") return item.disciplina;
      if (header.value === "cause") return item.cause;
      if (header.value === "status") return item.status;
      if (header.value === "date") return this.formatCreatedAt(item.date);
      return item[header.value] || '';
    },
    
    formatCreatedAt(dateString) {
      return helper.formatCreatedAt(dateString);
    },
    
    formatDate(dateString) {
      if (!dateString) return '';
      return new Date(dateString).toLocaleString('ro-RO', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    
    getStatusColor(status) {
      const statusColors = {
        'Respins': 'error',
        'Emis de profesor': 'primary',
        'Aprobat de Decan': 'info',
        'Aprobat de Rector': 'purple',
        'Îndeplinit': 'success',
        'În proces lucrare': 'orange',
        'În proces': 'warning'
      };
      
      if (!status) return 'grey';
      
      for (const [key, value] of Object.entries(statusColors)) {
        if (status.includes(key)) {
          return value;
        }
      }
      
      return 'grey';
    },
    
    handleTableOptionsUpdate(options) {
      const sortBy = options.sortBy && options.sortBy.length ? options.sortBy : ["date"];
      const sortDesc = options.sortDesc && options.sortDesc.length ? options.sortDesc : [true];
      
      this.pagination = {
        page: options.page || 1,
        itemsPerPage: options.itemsPerPage || 10,
        sortBy: sortBy,
        sortDesc: sortDesc
      };
      
      localStorage.setItem('teacherApprovalPaginationSettings', JSON.stringify({
        page: this.pagination.page,
        itemsPerPage: this.pagination.itemsPerPage,
        sortBy: this.pagination.sortBy,
        sortDesc: this.pagination.sortDesc
      }));
      
      this.$nextTick(() => {
        if (this.paginatedItems.length > 0) {
          const requestIds = this.paginatedItems
            .filter(item => item.requestId)
            .map(item => item.requestId);
            
          if (requestIds.length > 0) {
            this.fetchCommentsInBulk(requestIds);
          }
        }
      });
    },
    
    handleSearch(searchTerm) {
      this.search = searchTerm;
    },
    
    handleError(message) {
      this.errorMessage = message;
    },
    
    handleSuccess(message) {
      this.successMessage = message;
    },
    
    clearMessage() {
      this.errorMessage = "";
      this.successMessage = "";
    }
  },
  mounted() {
    // Load headers configuration from the JSON file
    this.headers = require("./teacherApprovalData.json").map(header => ({
      ...header,
      sortable: header.value !== 'actions' && header.value !== 'comments'
    }));
    
    // Set visible headers
    this.visibleHeaders = this.headers.filter(header => header.defaultVisible);
    
    // Set visibility flag for each header
    this.headers.forEach(header => {
      header.visible = header.defaultVisible;
    });
    
    // Restore pagination settings from localStorage
    const savedPaginationSettings = localStorage.getItem('teacherApprovalPaginationSettings');
    if (savedPaginationSettings) {
      try {
        const settings = JSON.parse(savedPaginationSettings);
        this.pagination = {
          page: settings.page || 1,
          itemsPerPage: settings.itemsPerPage || 10,
          sortBy: settings.sortBy || ["date"],
          sortDesc: settings.sortDesc || [true]
        };
      } catch (e) {
        console.error('Error parsing saved pagination settings:', e);
      }
    }
    
    // Set up refs
    this.$refs.approvalDetails.$on("error", this.handleError);
    this.$refs.approvalDetails.$on("success", this.handleSuccess);
    
    // Load approval data
    this.fetchApprovalData();
    
    // Watch for window unload to save pagination settings
    window.addEventListener('beforeunload', () => {
      localStorage.setItem('teacherApprovalPaginationSettings', JSON.stringify({
        page: this.pagination.page,
        itemsPerPage: this.pagination.itemsPerPage,
        sortBy: this.pagination.sortBy,
        sortDesc: this.pagination.sortDesc
      }));
    });
  },
  watch: {
    filteredData: {
      handler(newVal) {
        if (!this.isLoading && newVal.length > 0) {
          const requestIds = newVal
            .filter(item => item.requestId)
            .map(item => item.requestId);
          
          if (requestIds.length > 0) {
            this.$nextTick(() => {
              this.fetchCommentsInBulk(requestIds);
            });
          }
        }
      },
      deep: true
    },
    pagination: {
      handler(newVal, oldVal) {
        if (newVal.page !== oldVal.page || newVal.itemsPerPage !== oldVal.itemsPerPage) {
          if (this.paginatedItems.length > 0) {
            const requestIds = this.paginatedItems
              .filter(item => item.requestId)
              .map(item => item.requestId);
              
            if (requestIds.length > 0) {
              this.fetchCommentsInBulk(requestIds);
            }
          }
        }
      },
      deep: true
    }
  }
};
</script>

<style scoped>
.teacher-approval .v-data-table::v-deep tbody td {
  font-size: 18px;
}
.v-data-table::v-deep th {
  font-size: 20px !important;
  background-color: #2d8659 !important;
  color: white !important;
  white-space: nowrap;
}
.light-green-row {
  background-color: #ecf9f2;
}
.red-row {
  background-color: #f29daa;
}
@media (max-width: 600px) {
  .hide-on-mobile {
    display: none;
  }
}
.comments-wrapper {
  max-height: 150px;
  overflow-y: auto;
  padding: 4px;
  min-height: 60px; /* Increased minimum height to prevent layout shifts */
}

.compact-timeline {
  padding-top: 0;
}

.compact-item {
  padding-bottom: 8px !important;
}

.comment-header {
  display: flex;
  align-items: center;
  font-size: 0.85rem;
}

.user-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 150px;
}

.comment-body {
  font-size: 0.85rem;
  color: #666;
  margin: 2px 0;
  white-space: normal;
  word-break: break-word;
}

.comment-date {
  font-size: 0.75rem;
  color: #999;
}

.no-comments {
  text-align: center;
  color: #999;
  padding: 10px;
  font-style: italic;
}
</style>
